import { DocumentExtraction } from '@/modules/ai/models/DocumentExtraction';
import { DocumentCollection } from '@/modules/document/models/DocumentCollection';
import { Estate } from '@/modules/estate/models/Estate';
import { EstateTaskWillStatus } from '@/modules/estatetask/enums/EstateTaskWillStatus';
import { EstateTask } from '@/modules/estatetask/models/EstateTask';
import { EditorTemplateDocument } from '@/modules/export/_modules/editor/models/EditorTemplateDocument';
import { Note } from '@/modules/note/models/Note';
import { Codicil } from '@/modules/will/models/Codicil';
import { WillAdditionalNeed } from '@/modules/will/models/WillAdditionalNeed';
import dayjs from 'dayjs';
import { DateFormat } from '/@/modules/core/app/constants/date.constants';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { LocaleType } from '/@/modules/user/constants/LocaleType';

export class Will extends BaseEntityModel {
  id?: number;
  estateId?: number;
  hasAdditionalNeeds?: boolean;
  hasSubmittedAdditionalNeeds?: boolean;
  extraRequirements?: string;
  shouldRegenerate?: boolean;
  location?: string;
  isNotarial?: boolean;
  datedAt?: string;
  dateSigned?: string;
  affidavitDateSignedAt?: string;
  proofOfWillDateSignedAt?: string;
  isWitnessBeneficiary?: boolean;
  relationToDeceased?: string;
  estate?: Relationship<Estate | null>;
  documents: Relationship<DocumentCollection>;
  estateTask?: Relationship<EstateTask | null>;
  codicil?: Relationship<Codicil | null>;
  willAdditionalNeeds?: Relationship<WillAdditionalNeed[] | null>;
  notes?: Relationship<Note[] | null>;
  editorTemplateDocument?: Relationship<EditorTemplateDocument | null>;

  constructor(model: Partial<Will> = {}) {
    super(model);

    this.id = model.id;
    this.estateId = model.estateId;
    this.hasAdditionalNeeds = model.hasAdditionalNeeds;
    this.hasSubmittedAdditionalNeeds = model.hasSubmittedAdditionalNeeds;
    this.extraRequirements = model.extraRequirements;
    this.shouldRegenerate = model.shouldRegenerate;
    this.location = model.location;
    this.isNotarial = model.isNotarial;
    this.datedAt = model.datedAt;
    this.dateSigned = model.dateSigned;
    this.affidavitDateSignedAt = model.affidavitDateSignedAt;
    this.proofOfWillDateSignedAt = model.proofOfWillDateSignedAt;
    this.isWitnessBeneficiary = model.isWitnessBeneficiary;
    this.relationToDeceased = model.relationToDeceased;

    this.estate = Estate.mapModelOrNull(model.estate);
    this.documents = DocumentCollection.mapModel(model.documents);
    this.estateTask = EstateTask.mapModelOrNull(model.estateTask);
    this.codicil = Codicil.mapModelOrNull(model.codicil);
    this.willAdditionalNeeds = WillAdditionalNeed.mapModelsOrNull(model.willAdditionalNeeds);
    this.notes = Note.mapModelsOrNull(model.notes);
    this.editorTemplateDocument = EditorTemplateDocument.mapModelOrNull(
      model.editorTemplateDocument
    );
  }

  getModelName(): ModelType {
    return ModelType.WILL;
  }

  get documentUrl(): string | undefined {
    return this.documents?.willDocument?.url;
  }

  get documentName(): string | undefined {
    return this.documents?.willDocument?.name;
  }

  get documentUpdatedAt(): string | undefined {
    return dayjs(this.documents.willDocument?.updatedAt).format(DateFormat.ISO);
  }

  get documentSignedUrl(): string | undefined {
    return this.documents.willDocumentSigned?.url;
  }

  get documentSignedName(): string | undefined {
    return this.documents.willDocumentSigned?.name;
  }

  get documentAffidavitOfExecutionUrl(): string | undefined {
    return this.documents.affidavitOfExecutionDocument?.url;
  }

  get documentProofOfWillSignedUrl(): string | undefined {
    return this.documents.proofOfWillDocumentSigned?.url;
  }

  get documentProofOfWillSignedName(): string | undefined {
    return this.documents.proofOfWillDocumentSigned?.name;
  }

  get documentAffidavitOfExecutionSignedUrl(): string | undefined {
    return this.documents.affidavitOfExecutionDocumentSigned?.url;
  }

  get documentAffidavitOfExecutionSignedName(): string | undefined {
    return this.documents.affidavitOfExecutionDocumentSigned?.name;
  }

  get isAccepted(): boolean {
    return this.estateTask?.status === EstateTaskWillStatus.ACCEPTED.key;
  }

  getLocaleFormattedCreatedAt(locale: string): string {
    return dayjs(this.createdAt).format(
      locale === LocaleType.ENGLISH ? DateFormat.MONTH_DAY_YEAR : DateFormat.DAY_MONTH_YEAR
    );
  }

  get hasValidatedStep895621WillExecutorsExtraction(): boolean {
    return !!this.step895621WillExecutorsExtraction?.isValidated;
  }

  get step895621WillExecutorsExtraction(): DocumentExtraction | undefined {
    return this.documents.willDocument?.documentExtractions?.items?.find(
      (extraction: DocumentExtraction) => extraction.isStep895621WillExecutorsType
    );
  }

  get hasAllSecureWillAndCodicilDocumentExtractions(): boolean {
    return !!this.documents.willDocument?.documentExtractions
      ?.hasAllSecureWillAndCodicilExtractions;
  }

  get hasAtLeastOneSecureWillAndCodicilDocumentExtraction(): boolean {
    return !!this.documents.willDocument?.documentExtractions
      ?.hasAtLeastOneSecureWillAndCodicilExtraction;
  }

  get hasAllValidatedSecureWillAndCodicilDocumentExtractions(): boolean {
    return !!this.documents.willDocument?.documentExtractions
      ?.hasAllValidatedSecureWillAndCodicilExtractions;
  }
}
